import { useState, useCallback } from 'react'
import Store from '../server/store/Store'

/** @type {Store} Singleton store instance for localStorage operations */
const store = new Store()

/**
 * A React hook for persisting state in localStorage with automatic synchronization.
 *
 * This hook provides a useState-like API that automatically persists values
 * to localStorage using the Store class. Values are JSON serialized/deserialized
 * automatically.
 *
 * @template T
 * @param {string} key - The localStorage key to store the value under
 * @param {T} initialValue - The initial value to use if no stored value exists
 * @returns {[T, (value: T | ((prev: T) => T)) => void, () => void]} A tuple containing:
 *   - The current stored value
 *   - A setter function (accepts a value or updater function)
 *   - A remove function to clear the stored value and reset to initialValue
 *
 * @example
 * const [theme, setTheme, removeTheme] = useLocalStorage('theme', 'light')
 *
 * @example
 * // With updater function
 * const [count, setCount] = useLocalStorage('count', 0)
 * setCount(prev => prev + 1)
 */
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    const item = store.get(key)
    return item !== null ? item : initialValue
  })

  const setValue = useCallback(
    value => {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      store.set(key, valueToStore)
    },
    [key, storedValue],
  )

  const removeValue = useCallback(() => {
    store.remove(key)
    setStoredValue(initialValue)
  }, [key, initialValue])

  return [storedValue, setValue, removeValue]
}

export default useLocalStorage
